import React, { useState, useRef, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import styled, { css } from 'styled-components'
import callApi from '../../helpers/callapi'
import config from '../../config'
import Colors from '../../constants/Colors'
import * as Base64 from '../../helpers/base64'
import UploadContent from './UploadContent'
import ScanContent from './ScanContent'
import VerifyContent from './VerifyContent'
import { NOT_FOUND_LOTTERY_ID } from './constants'
import Cookies from 'js-cookie'
import { H2 } from '../../components/FontStyle'
import { getCurrentDateTime } from '../../helpers/getCurrentDateTime'
import Swal from 'sweetalert2'

const RecheckPrizes = () => {
  const dispatch = useDispatch()
  const uploadRef = useRef()

  const [buttonName, setButtonName] = useState('')
  const [content, setContent] = useState('upload')

  const [files, setFiles] = useState([])
  const [lotteries, setLotteries] = useState([])

  const isDisabledButton = useMemo(() => {
    const scan =
      lotteries.length === 0 || lotteries.every((v) => v.lotteryId !== '' && v.lotteryId !== NOT_FOUND_LOTTERY_ID)
    const verify =
      lotteries.length === 0 || lotteries.some((v) => v.lotteryId === '' || v.lotteryId === NOT_FOUND_LOTTERY_ID)

    return { scan, verify }
  }, [lotteries])

  useEffect(() => {
    const getLotteries = async () => {
      const newLotteries = await Promise.all(
        Object.keys(files).map(async (key) => {
          return {
            url: URL.createObjectURL(files[key]),
            fileName: files[key].name,
            isPrize: false,
            lotteryId: '',
            base64: await Base64.getBase64(files[key]),
          }
        }),
      )

      setLotteries(newLotteries)
    }

    getLotteries()
  }, [files, setLotteries])

  const clearAllState = () => {
    setContent('upload')
    setButtonName('')
    setFiles([])
    setLotteries([])
    uploadRef.current.value = null
  }

  const handleClickUploadButton = () => {
    uploadRef.current.click()
  }

  const handleClickScanButton = async () => {
    try {
      dispatch({ type: 'loading_show' })

      if (lotteries.length > 0) {
        const requests = []
        for (let i = 0; i < lotteries.length; i++) {
          requests.push(
            callApi({
              fullURL: `${config.dataMatrixDetectionApi}/detect`,
              method: 'post',
              body: { base64: lotteries[i].base64 },
            }),
          )
        }

        const lottoIds = []
        for (let i = 0; i < requests.length; i++) {
          try {
            const response = await requests[i]
            if (!response.data) {
              throw new Error('No data')
            }

            const arrNumber = response.data.split('-')

            arrNumber.pop()
            lottoIds.push(arrNumber.join('-'))
          } catch (error) {
            console.error(error)
            lottoIds.push(NOT_FOUND_LOTTERY_ID)
          }
        }

        const newLotteries = [...lotteries]
        for (let i = 0; i < lottoIds.length; i++) {
          newLotteries[i].lotteryId = lottoIds[i]
        }

        setLotteries(newLotteries)
        setButtonName('scan')
        setContent('scan')

        dispatch({ type: 'loading_hide' })
      }
    } catch (error) {
      console.error(error)
      dispatch({ type: 'loading_hide' })
    }
  }

  const handleClickVerifyButton = async () => {
    try {
      dispatch({ type: 'loading_show' })

      const lotteryIds = lotteries.map((lotto) => lotto.lotteryId)

      const response = await fetch(`${config.apiAdminGateway}/api/v2/prizes/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: `Bearer ${Cookies.get('accessToken')}`,
        },
        body: JSON.stringify({ ids: lotteryIds }),
      })

      const { data: prizeGroup } = await response.json()

      let newLotteries = []
      Object.keys(prizeGroup).forEach((prizeName) => {
        const { data: lotteryIds } = prizeGroup[prizeName]

        lotteries.forEach((lotto, index) => {
          if (lotteryIds.includes(lotto.lotteryId)) {
            const isPrize = prizeName !== 'notFound'
            newLotteries[index] = { ...lotto, isPrize, prizeName }
          }
        })
      })

      setLotteries(newLotteries)
      setButtonName('verify')
      setContent('verify')

      dispatch({ type: 'loading_hide' })
    } catch (error) {
      console.error(error)
      dispatch({ type: 'loading_hide' })
      Swal.fire('เกิดข้อผิดพลาด', error.message, 'error')
    }
  }

  const handleClickRemoveSomeLottery = (index) => {
    const newLotteries = lotteries.filter((_, i) => i !== index)

    setLotteries(newLotteries)
  }

  const handleContent = (content) => {
    switch (content) {
      case 'upload':
        return <UploadContent lotteries={lotteries} handleClickRemoveSomeLottery={handleClickRemoveSomeLottery} />
      case 'scan':
        return (
          <ScanContent
            lotteries={lotteries}
            setLotteries={setLotteries}
            handleClickRemoveSomeLottery={handleClickRemoveSomeLottery}
          />
        )
      case 'verify':
        return (
          <VerifyContent
            lotteries={lotteries}
            setLotteries={setLotteries}
            handleClickRemoveSomeLottery={handleClickRemoveSomeLottery}
          />
        )
      default:
        return <UploadContent lotteries={lotteries} handleClickRemoveSomeLottery={handleClickRemoveSomeLottery} />
    }
  }

  return (
    <RecheckPrizeContent>
      <div className="recheck-header">
        <H2>ตรวจสอบลอตเตอรี่ก่อนขึ้นเงิน</H2>
        <H2>{getCurrentDateTime()}</H2>
      </div>
      <input
        hidden
        type="file"
        multiple
        name="input-lotteries"
        onChange={(e) => {
          setFiles(e.target.files)
        }}
        onClick={clearAllState}
        ref={uploadRef}
      />
      <ButtonBox>
        <Button onClick={handleClickUploadButton} isActived={buttonName === 'upload'}>
          Upload Lottery
        </Button>
        <Button onClick={handleClickScanButton} disabled={isDisabledButton.scan} isActived={buttonName === 'scan'}>
          Scan Lottery
        </Button>
        <Button
          onClick={handleClickVerifyButton}
          disabled={isDisabledButton.verify}
          isActived={buttonName === 'verify'}
        >
          Verify Lottery
        </Button>
      </ButtonBox>
      <br />
      {handleContent(content)}
    </RecheckPrizeContent>
  )
}

export default RecheckPrizes

const RecheckPrizeContent = styled.div`
  width: 100%;
  height: 100%;
  padding: 20px;
  background: ${Colors.WHITE};

  .recheck-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 24px;
  }
`

const ButtonBox = styled.div`
  width: 100%;
  height: 126px;
  display: flex;
  justify-content: space-between;
  gap: 10px;
`

const Button = styled.button`
  width: 100%;
  height: 126px;
  padding: 34px;
  border-radius: 8px;
  text-align: center;
  font-size: 16px;
  font-family: Prompt-SemiBold;
  background: ${Colors.WHITE};
  border: solid 1px ${Colors.LIGHT_GREY2};

  &:hover {
    border: solid 1px ${Colors.SOFT_BLUE};
    color: ${Colors.SOFT_BLUE};
  }

  :disabled {
    background: #fbfbfb;
    border: solid 1px ${Colors.LIGHT_GREY};
    color: ${Colors.LIGHT_GREY2};
    fill: ${Colors.LIGHT_GREY2};
  }

  ${({ isActived }) =>
    isActived &&
    css`
      background: ${Colors.DARK_BLUE};
      border: solid 1px ${Colors.DARK_BLUE};
      color: ${Colors.WHITE};
    `}
`
